import { useRef, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import {
  usePengetahuanSaya, useUnggahPengetahuan, useUjiAmbil, useUjiPercakapan,
} from '../../../domains/tenancy/api'
import { useCari } from '../../../shared/lib/cari'
import { tanggal } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, BilahTabel, Body, Chip, Gagal, Isian, KepalaHalaman, Kolom2, Kosong,
  Memuat, Panel, Sub, Tabel, Tombol,
} from '../../../shared/ui'

/**
 * PENGETAHUAN — bahan yang dipakai bot menjawab.
 *
 * DIBACA SAJA untuk sekarang, dan itu dinyatakan di layar. Memuat dokumen masih lewat
 * `make muat-knowledge` dari berkas; membangun unggahannya menuntut penanganan berkas,
 * pemotongan chunk, dan embedding ulang — pekerjaan tersendiri.
 *
 * Yang TIDAK ditampilkan: isi dokumennya. Ia bisa memuat harga dan syarat kontrak, dan
 * layar ini menjawab "bot punya bahan apa", bukan "apa isinya".
 */
/**
 * UNGGAH DOKUMEN — satu tombol, satu berkas, langsung terpakai bot.
 *
 * Sampai sekarang satu-satunya jalan `make muat-knowledge` lewat SSH, dan admin tenant
 * tidak punya SSH. Pengetahuan adalah SATU-SATUNYA bahan yang boleh dipakai bot menjawab;
 * layar yang menampilkannya tanpa cara menambahnya berhenti di setengah jalan.
 *
 * Backend memenggal dan meng-embed dalam permintaan yang sama, jadi dokumen yang baru
 * diunggah langsung punya potongan. Itu memakan beberapa detik — tombolnya menyebut
 * bahwa ia sedang memproses, bukan cuma "mengunggah", supaya jeda itu tidak terbaca
 * sebagai macet.
 */
function Unggah({ t }) {
  const pilih = useRef(null)
  const kirim = useUnggahPengetahuan()

  return (
    <div className="flex flex-col items-end gap-1.5">
      <input
        ref={pilih}
        type="file"
        className="hidden"
        accept=".txt,.md,.markdown,.csv,.json"
        onChange={(e) => {
          const f = e.target.files?.[0]
          // Nilai input DIKOSONGKAN supaya memilih berkas yang SAMA dua kali tetap
          // memicu `change`. Tanpa ini, unggah ulang sesudah memperbaiki isinya diam
          // saja, dan orangnya mengira tombolnya rusak.
          e.target.value = ''
          if (f) kirim.mutate(f)
        }}
      />
      <Tombol disabled={kirim.isPending} onClick={() => pilih.current?.click()}>
        {kirim.isPending ? t('ta.pengMemproses') : t('ta.pengUnggah')}
      </Tombol>
      <Sub className="text-right max-w-[34ch]">{t('ta.pengJenis')}</Sub>
      {kirim.isError && (
        <Bendera nada="bad" role="alert">{kirim.error?.message}</Bendera>
      )}
      {kirim.isSuccess && (
        <Bendera nada="ok">
          {t('ta.pengTerunggah', {
            nama: kirim.data?.dokumen?.source_file,
            n: kirim.data?.potongan_baru ?? 0,
          })}
        </Bendera>
      )}
    </div>
  )
}

/** Ekstensi berkas, huruf kecil — "profil.PDF" dan "profil.pdf" satu jenis yang sama. */
function jenisBerkas(nama) {
  const titik = String(nama ?? '').lastIndexOf('.')
  if (titik < 1) return '—'
  return nama.slice(titik + 1).toLowerCase()
}

export default function Pengetahuan() {
  const { t, locale } = useBahasa()
  const q = usePengetahuanSaya()
  const { cari, setCari, hasil } = useCari(q.data, (b) => [b.source_file, b.version])

  if (q.isLoading) return <Memuat tinggi={5} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />

  const baris = q.data ?? []
  const aktif = baris.filter((b) => b.is_active).length
  // POTONGAN DARI DOKUMEN AKTIF SAJA. Dokumen nonaktif potongannya masih ada di tabel
  // tapi tidak pernah diambil bot, jadi menjumlahkannya melebih-lebihkan bahan yang
  // benar-benar dipakai menjawab.
  const potongan = baris.reduce((n, b) => n + (b.is_active ? (b.potongan ?? 0) : 0), 0)

  return (
    <>
      <KepalaHalaman
        judul={t('ta.navPengetahuan')}
        keterangan={t('ta.pengCatatan')}
        aksi={<Unggah t={t} />}
      />

      <Panel judul={t('ta.pengRingkasJudul', { dok: baris.length, potongan })}>
      {baris.length === 0 ? (
        <Kosong judul={t('ta.pengKosong')} keterangan={t('ta.pengKosongKet')} />
      ) : (
        <>
          <BilahTabel
            cari={{ nilai: cari, set: setCari, placeholder: t('umum.cari') }}
            ringkas={t('ta.pengRingkas', { aktif, total: baris.length })}
          />
          {hasil.length === 0 ? (
            <Kosong judul={t('umum.tidakCocok')} />
          ) : (
          <Tabel kepala={[t('ta.kolDokumen'), t('ta.kolJenis'), t('ta.kolPotongan'),
                          t('ta.kolDiproses'), t('ta.kolDipakaiBot')]}>
            {hasil.map((b) => (
              <Baris key={b.id}>
                <td className={b.is_active ? undefined : 'text-ink3'}>
                  {b.source_file}
                  {/* Versi turun jadi sub-label. Ia jarang jadi alasan orang membuka
                      layar ini, dan sebagai kolom penuh ia memakan lebar dari "Diproses"
                      yang dibaca tiap kali dokumen diganti. */}
                  <span className="block font-mono text-[10.5px] text-ink3">
                    {b.version}
                  </span>
                </td>
                <td className="font-mono text-[11.5px] text-ink2">{jenisBerkas(b.source_file)}</td>
                {/* NOL POTONGAN = dokumennya ada, tapi bot tidak bisa memakainya sama
                    sekali. Tanpa kolom ini ia terlihat PERSIS seperti dokumen yang sudah
                    di-ingest — aktif, panjang, bernomor versi. */}
                <td className="tabular">
                  {b.potongan === 0
                    ? <Chip nada="bad">{t('ta.belumDiproses')}</Chip>
                    : b.potongan}
                </td>
                {/* DIPROSES, bukan diunggah. Dokumen yang sudah diganti tapi belum
                    di-ingest ulang menampilkan tanggal LAMA di sini — dan itu tepat yang
                    perlu terbaca: isinya baru, yang dipakai bot masih yang lama. */}
                <td className="font-mono text-[11.5px] text-ink2">
                  {b.diproses
                    ? tanggal(b.diproses, locale)
                    : <Chip nada="bad">{t('ta.belumDiproses2')}</Chip>}
                </td>
                <td>
                  <Chip nada={b.is_active ? 'ok' : 'idle'}>
                    {b.is_active ? t('umum.ya') : t('umum.tidak')}
                  </Chip>
                </td>
              </Baris>
            ))}
          </Tabel>
          )}
        </>
      )}
      </Panel>

      <Kolom2>
        <UjiAmbil t={t} />
        <UjiPercakapan t={t} />
      </Kolom2>

      <Bendera nada="info" judul={t('ta.duaUjiJudul')} className="max-w-[80ch]">
        {t('ta.duaUjiIsi')}
      </Bendera>
    </>
  )
}

/**
 * UJI AMBIL — apakah pertanyaan ini menemukan bahan?
 *
 * MURAH dan karena itu boleh dicoba berkali-kali: satu embedding, satu pencarian vektor,
 * nol model bahasa. Yang dicari justru pertanyaan yang TIDAK menemukan apa-apa.
 */
function UjiAmbil({ t }) {
  const [q, setQ] = useState('')
  const uji = useUjiAmbil()
  const d = uji.data

  return (
    <Panel judul={t('ta.ujiAmbilJudul')} catatan={t('ta.ujiAmbilCatatan')}>
      <Body className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Isian
            className="max-w-[420px]"
            placeholder={t('ta.ujiPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) uji.mutate(q.trim()) }}
          />
          <Tombol disabled={!q.trim() || uji.isPending} onClick={() => uji.mutate(q.trim())}>
            {uji.isPending ? t('umum.menunggu') : t('ta.ujiJalankan')}
          </Tombol>
        </div>

        {uji.isError && <Bendera nada="bad" role="alert">{uji.error?.message}</Bendera>}

        {d && (d.potongan === 0 ? (
          // NOL POTONGAN adalah temuan, bukan hasil kosong. Bot tetap akan menjawab
          // pertanyaan ini — tanpa bahan, dengan keyakinan tinggi, dan tanpa memberi
          // tahu tim. Itu BUGS VIII-1, dan panel ini ada untuk menemukannya lebih dulu.
          <Bendera nada="bad" role="alert" judul={t('ta.ujiNolJudul')}>
            {t('ta.ujiNolIsi')}
          </Bendera>
        ) : (
          <div className="flex flex-col gap-2">
            <Bendera nada="ok">
              {t('ta.ujiKetemu', { n: d.potongan, karakter: d.karakter })}
            </Bendera>
            {d.cuplikan.map((c, i) => (
              <p key={i} className="m-0 bg-surface2 border border-line rounded px-3 py-2
                                    text-[11.5px] text-ink2 font-mono whitespace-pre-wrap">
                {c}
              </p>
            ))}
          </div>
        ))}
      </Body>
    </Panel>
  )
}

/**
 * UJI PERCAKAPAN — apakah jawabannya MEMAKAI bahan yang ditemukan, atau mengarang.
 *
 * MAHAL: ia memanggil model sungguhan dan menagih biaya. Karena itu satu pertanyaan per
 * jalan, dan layar menyebutnya di muka.
 *
 * Yang diperiksa SATU hal yang bisa dinilai mesin — bukan "bagus atau tidak", karena itu
 * selera dan mesin tidak bisa menilainya.
 */
function UjiPercakapan({ t }) {
  const [q, setQ] = useState('')
  const uji = useUjiPercakapan()
  const d = uji.data

  return (
    <Panel judul={t('ta.ujiPercakapanJudul')} catatan={t('ta.ujiPercakapanCatatan')}>
      <Body className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Isian
            className="max-w-[420px]"
            placeholder={t('ta.ujiPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Tombol
            jenis="sekunder"
            disabled={!q.trim() || uji.isPending}
            onClick={() => uji.mutate(q.trim())}
          >
            {uji.isPending ? t('umum.menunggu') : t('ta.ujiJalankanMahal')}
          </Tombol>
        </div>

        {uji.isError && <Bendera nada="bad" role="alert">{uji.error?.message}</Bendera>}

        {d && (
          <div className="flex flex-col gap-2">
            <Bendera nada={d.memakai_konteks ? 'ok' : 'bad'}>
              {t(d.memakai_konteks ? 'ta.ujiPakaiKonteks' : 'ta.ujiMengarang',
                 { n: d.potongan })}
            </Bendera>
            <p className="m-0 bg-surface2 border border-line rounded px-3 py-2
                          text-[12px] text-ink2 whitespace-pre-wrap">{d.jawaban}</p>
          </div>
        )}
      </Body>
    </Panel>
  )
}
